import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OBIEKTY, REGIONY, RANKING, KRYTERIA, PRZYSTANIE_STATS } from '../data/przystanie';
import { PRZYSTANIE_COPY } from '../seo/przystanieCopy';
import { breadcrumbJsonLd, faqJsonLd, absoluteUrl } from '../seo/siteMeta';
import { odmien } from '../lib/odmiana';
import JsonLd from '../seo/JsonLd';
import Faq from './Faq';
import styles from '../css/modules/Przystanie.module.css';

/**
 * Katalog przystani i klubów żeglarskich.
 *
 * Jeden komponent obsługuje stronę zbiorczą i podstrony województw - różnią się
 * zakresem listy, nie układem. Dane pochodzą z OpenStreetMap i są generowane
 * przez scripts/przystanie/gen.py.
 *
 * Sekcja "Skąd mamy te dane" jest wymagana licencją ODbL - nie usuwaj jej.
 */

const PRZYSTANIE_FORMY = ['przystań', 'przystanie', 'przystani'];

const FAQ = [
  {
    question: 'Skąd pochodzą dane o przystaniach?',
    answer:
      'Z OpenStreetMap, uzupełnione o odwrotne geokodowanie Nominatim. Publikujemy wyłącznie to, co jest w źródle - nie zmyślamy adresów ani telefonów. Jeżeli obiektu brakuje albo dane są nieaktualne, najskuteczniej poprawić je bezpośrednio w OpenStreetMap.',
  },
  {
    question: 'Czym jest punktacja przy obiekcie?',
    answer:
      'To miara kompletności danych, a nie ocena jakości przystani. Punktujemy wyłącznie rzeczy sprawdzalne: podany telefon, własną stronę, godziny otwarcia, pełny adres, liczbę miejsc postojowych i udogodnienia opisane w OpenStreetMap. Serwis nie zbiera opinii i nie publikuje ocen.',
  },
  {
    question: 'Czy w katalogu są wszystkie przystanie w Polsce?',
    answer:
      'Nie. Katalog obejmuje obiekty opisane w OpenStreetMap, więc mniejsze przystanie i kluby bez wpisu w mapie mogą go nie mieć. Lista rośnie razem z danymi w OSM.',
  },
];

const Karta = ({ o }) => (
  <li className={styles.karta}>
    <div className={styles.kartaGlowa}>
      <h3 className={styles.kartaNazwa}>{o.n}</h3>
      <span className={o.kat === 'przystan' ? styles.tagPrzystan : styles.tagSzkola}>
        {o.kat === 'przystan' ? 'przystań' : 'klub / szkoła'}
      </span>
    </div>

    {o.a && <p className={styles.kartaAdres}>{o.a}</p>}
    {!o.a && o.c && <p className={styles.kartaAdres}>{o.c}</p>}

    <dl className={styles.kartaDane}>
      {o.t && (
        <>
          <dt>Telefon</dt>
          <dd><a href={`tel:${o.t.replace(/\s/g, '')}`}>{o.t}</a></dd>
        </>
      )}
      {o.h && (<><dt>Godziny</dt><dd>{o.h}</dd></>)}
      {o.m && (<><dt>Miejsca</dt><dd>{o.m}</dd></>)}
      {o.e && (<><dt>E-mail</dt><dd><a href={`mailto:${o.e}`}>{o.e}</a></dd></>)}
      {o.w && (
        <>
          <dt>Strona</dt>
          <dd>
            <a href={o.w} target="_blank" rel="noopener noreferrer nofollow">
              {o.w.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
            </a>
          </dd>
        </>
      )}
    </dl>

    {o.d && <p className={styles.kartaOpis}>{o.d}</p>}

    {o.k.length > 0 && (
      <ul className={styles.kryteria}>
        {o.k.map(k => (
          <li key={k} className={styles.kryterium}>{KRYTERIA[k]?.nazwa || k}</li>
        ))}
      </ul>
    )}
  </li>
);

const Lista = ({ indeksy }) => {
  // Grupujemy po miejscowości - bez tego lista województwa to kilkadziesiąt
  // kart bez żadnego porządku.
  const wgMiasta = useMemo(() => {
    const mapa = new Map();
    indeksy.forEach(i => {
      const o = OBIEKTY[i];
      const klucz = o.c || 'Pozostałe';
      if (!mapa.has(klucz)) mapa.set(klucz, []);
      mapa.get(klucz).push(o);
    });
    return [...mapa.entries()].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0], 'pl'));
  }, [indeksy]);

  return (
    <>
      {wgMiasta.map(([miasto, obiekty]) => (
        <section key={miasto} className={styles.grupa}>
          <h3 className={styles.grupaNazwa}>
            {miasto} <span className={styles.grupaLiczba}>{obiekty.length}</span>
          </h3>
          <ul className={styles.karty}>
            {obiekty.map(o => <Karta key={o.id} o={o} />)}
          </ul>
        </section>
      ))}
    </>
  );
};

const Zrodlo = () => (
  <section className={styles.zrodlo}>
    <h2 className={styles.h2}>Skąd mamy te dane</h2>
    <p>
      Szkielet katalogu pochodzi z <a href="https://www.openstreetmap.org/copyright"
        target="_blank" rel="noopener noreferrer">OpenStreetMap</a> - nazwy, położenie,
      część adresów, telefonów i godzin otwarcia. Dane są udostępniane na licencji
      Open Database License (ODbL), © współtwórcy OpenStreetMap. Przynależność
      administracyjną ustalamy odwrotnym geokodowaniem w Nominatim.
    </p>
    <p>
      Nie uzupełniamy danych z katalogów firm ani z konkurencyjnych map - to cudze
      bazy. Czego nie ma w źródle, tego nie ma na stronie. Znalazłeś błąd? Najszybciej
      poprawisz go bezpośrednio w OpenStreetMap, a zmiana trafi tu przy kolejnej
      aktualizacji katalogu.
    </p>
  </section>
);

const Hub = () => {
  const regiony = useMemo(
    () => Object.entries(REGIONY).sort((a, b) => a[1].nazwa.localeCompare(b[1].nazwa, 'pl')),
    []
  );

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: 'Przystanie', path: '/przystanie' },
      ])} />
      <JsonLd data={faqJsonLd(FAQ)} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Przystanie i kluby żeglarskie w Polsce według województw',
        numberOfItems: regiony.length,
        itemListElement: regiony.map(([slug, r], i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: r.nazwa,
          url: absoluteUrl(`/przystanie/wojewodztwo/${slug}`),
        })),
      }} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / Przystanie
      </nav>

      <h1 className={styles.h1}>Przystanie i kluby żeglarskie w Polsce</h1>
      <p className={styles.lead}>
        {PRZYSTANIE_STATS.razem} {odmien(PRZYSTANIE_STATS.razem, ['obiekt', 'obiekty', 'obiektów'])}{' '}
        w {PRZYSTANIE_STATS.regiony} województwach: {PRZYSTANIE_STATS.przystanie}{' '}
        {odmien(PRZYSTANIE_STATS.przystanie, PRZYSTANIE_FORMY)} i portów jachtowych oraz{' '}
        {PRZYSTANIE_STATS.szkoly} klubów i szkół żeglarskich. Dane z OpenStreetMap,
        bez ocen i bez opinii - pokazujemy wyłącznie to, co da się sprawdzić.
      </p>

      <ul className={styles.statystyki}>
        <li><strong>{PRZYSTANIE_STATS.telefony}</strong> z telefonem</li>
        <li><strong>{PRZYSTANIE_STATS.strony}</strong> z własną stroną</li>
        <li><strong>{PRZYSTANIE_STATS.regiony}</strong> województw</li>
      </ul>

      <h2 className={styles.h2}>Według województwa</h2>
      <div className={styles.regiony}>
        {regiony.map(([slug, r]) => (
          <Link key={slug} to={`/przystanie/wojewodztwo/${slug}`} className={styles.region}>
            <span className={styles.regionNazwa}>{r.nazwa}</span>
            <span className={styles.regionLiczba}>
              {r.idx.length} {odmien(r.idx.length, ['obiekt', 'obiekty', 'obiektów'])}
            </span>
          </Link>
        ))}
      </div>

      <h2 className={styles.h2}>Najlepiej opisane obiekty</h2>
      <p className={styles.info}>
        Kolejność wynika z kompletności danych, a nie z jakości przystani -
        wyżej stoją obiekty, o których wiadomo więcej.
      </p>
      <ul className={styles.karty}>
        {RANKING.slice(0, 12).map(i => <Karta key={OBIEKTY[i].id} o={OBIEKTY[i]} />)}
      </ul>

      <h2 className={styles.h2}>Najczęstsze pytania</h2>
      <Faq items={FAQ} />

      <Zrodlo />
    </div>
  );
};

const Region = ({ slug }) => {
  const region = REGIONY[slug];

  if (!region) {
    return (
      <div className={styles.wrap}>
        <h1 className={styles.h1}>Nie mamy takiego województwa w katalogu</h1>
        <Link className={styles.cta} to="/przystanie">Wróć do katalogu</Link>
      </div>
    );
  }

  const copy = PRZYSTANIE_COPY[slug];
  const zTelefonem = region.idx.filter(i => OBIEKTY[i].t).length;

  return (
    <div className={styles.wrap}>
      <JsonLd data={breadcrumbJsonLd([
        { name: 'Strona główna', path: '/' },
        { name: 'Przystanie', path: '/przystanie' },
        { name: region.nazwa, path: `/przystanie/wojewodztwo/${slug}` },
      ])} />
      <JsonLd data={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `Przystanie i kluby żeglarskie - ${region.nazwa}`,
        numberOfItems: region.idx.length,
        itemListElement: region.idx.slice(0, 30).map((i, poz) => ({
          '@type': 'ListItem',
          position: poz + 1,
          name: OBIEKTY[i].n,
        })),
      }} />

      <nav className={styles.breadcrumbs}>
        <Link to="/">Strona główna</Link> / <Link to="/przystanie">Przystanie</Link> / {region.nazwa}
      </nav>

      <h1 className={styles.h1}>Przystanie i kluby żeglarskie - {region.nazwa}</h1>
      <p className={styles.lead}>
        {region.idx.length} {odmien(region.idx.length, ['obiekt', 'obiekty', 'obiektów'])}{' '}
        w województwie {region.nazwa.toLowerCase()}, w tym {zTelefonem} z podanym telefonem.
        Dane pochodzą z OpenStreetMap.
      </p>

      {copy?.akapity?.map(a => <p key={a.slice(0, 40)} className={styles.akapit}>{a}</p>)}

      <Lista indeksy={region.idx} />

      <div className={styles.akcje}>
        <Link className={styles.ctaDrugi} to="/przystanie">Pozostałe województwa</Link>
      </div>

      <Zrodlo />
    </div>
  );
};

const Przystanie = () => {
  const { slug } = useParams();
  return slug ? <Region slug={slug} /> : <Hub />;
};

export default Przystanie;
